import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserRole } from '../hooks/useUserRole';
import { privacyNoticeApi, organizationApi } from '../services/api';
import {
  PrivacyNotice,
  PrivacyNoticeChannel,
  PrivacyNoticeStatus,
  Organization
} from '@dsgvo-buddy/types';
import {
  Title,
  Button,
  Table,
  TableColumn,
  TableRow,
  TableCell,
  Input,
  Select,
  Option,
  FlexBox,
  Text,
  Icon,
  ObjectStatus,
  BusyIndicator,
  Card,
  CardHeader
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';
import PageIntro from '../components/PageIntro';
import { pageIntros } from '../helpers/helpTexts';

const channelLabels: Record<PrivacyNoticeChannel, string> = {
  [PrivacyNoticeChannel.WEBSITE]: 'Website',
  [PrivacyNoticeChannel.APP]: 'App',
  [PrivacyNoticeChannel.APPLICATION_FORM]: 'Bewerbungsformular',
  [PrivacyNoticeChannel.EMPLOYMENT]: 'Beschäftigung',
  [PrivacyNoticeChannel.NEWSLETTER]: 'Newsletter',
  [PrivacyNoticeChannel.CUSTOM]: 'Benutzerdefiniert'
};

const statusConfig: Record<PrivacyNoticeStatus, { label: string; state: string }> = {
  [PrivacyNoticeStatus.DRAFT]: { label: 'Entwurf', state: 'None' },
  [PrivacyNoticeStatus.IN_REVIEW]: { label: 'In Prüfung', state: 'Information' },
  [PrivacyNoticeStatus.APPROVED]: { label: 'Freigegeben', state: 'Success' },
  [PrivacyNoticeStatus.ARCHIVED]: { label: 'Archiviert', state: 'None' }
};

export default function PrivacyNoticeListPage() {
  const navigate = useNavigate();
  const { canCreate } = useUserRole();
  const [notices, setNotices] = useState<PrivacyNotice[]>([]);
  const [filteredNotices, setFilteredNotices] = useState<PrivacyNotice[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [channelFilter, setChannelFilter] = useState<PrivacyNoticeChannel | ''>('');
  const [statusFilter, setStatusFilter] = useState<PrivacyNoticeStatus | ''>('');

  useEffect(() => { loadOrganizations(); }, []);
  useEffect(() => { if (currentOrg) { loadNotices(); } }, [currentOrg]);
  useEffect(() => { applyFilters(); }, [notices, searchTerm, channelFilter, statusFilter]);

  const loadOrganizations = async () => {
    const response = await organizationApi.getAll();
    if (response.success && response.data) {
      if (response.data.length > 0) setCurrentOrg(response.data[0]);
    }
    setLoading(false);
  };

  const loadNotices = async () => {
    if (!currentOrg) return;
    setLoading(true);
    const response = await privacyNoticeApi.getByOrganization(currentOrg.id);
    if (response.success && response.data) setNotices(response.data);
    setLoading(false);
  };

  const applyFilters = () => {
    let filtered = [...notices];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(n =>
        n.title.toLowerCase().includes(term) ||
        channelLabels[n.channel].toLowerCase().includes(term)
      );
    }
    if (channelFilter) filtered = filtered.filter(n => n.channel === channelFilter);
    if (statusFilter) filtered = filtered.filter(n => n.status === statusFilter);
    setFilteredNotices(filtered);
  };

  if (loading && !currentOrg) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  return (
    <div className="page-layout">
      {/* Page Header */}
      <FlexBox justifyContent="SpaceBetween" alignItems="Center" style={{ marginBottom: '1.5rem' }}>
        <div>
          <Title level="H3" style={{ marginBottom: '0.25rem' }}>Datenschutzhinweise</Title>
          <Text style={{ color: 'var(--sapNeutralTextColor)' }}>
            Datenschutzerklärungen und Hinweistexte für alle Kanäle
          </Text>
        </div>
        {canCreate && (
          <Button icon="add" design="Emphasized" onClick={() => navigate('/privacy-notices/new')}>
            Neuer Datenschutzhinweis
          </Button>
        )}
      </FlexBox>

      <PageIntro {...pageIntros['privacy-notices']} storageKey="privacy-notices" />

      {/* Filter Bar */}
      <FlexBox
        alignItems="End"
        wrap="Wrap"
        style={{
          gap: '0.75rem',
          marginBottom: '1rem',
          padding: '1rem',
          background: 'var(--sapObjectHeader_Background, var(--sapBaseColor))',
          borderRadius: '0.5rem',
          border: '1px solid var(--sapGroup_TitleBorderColor, #d9d9d9)'
        }}
      >
        <div style={{ flex: '1 1 300px', minWidth: '200px' }}>
          <Text style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>Suche</Text>
          <Input
            placeholder="Titel, Kanal..."
            icon={<Icon name="search" />}
            value={searchTerm}
            onInput={(e: any) => setSearchTerm(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ flex: '0 0 220px' }}>
          <Text style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>Kanal</Text>
          <Select onChange={(e: any) => setChannelFilter(e.detail.selectedOption.value)} style={{ width: '100%' }}>
            <Option value="">Alle Kanäle</Option>
            <Option value={PrivacyNoticeChannel.WEBSITE}>Website</Option>
            <Option value={PrivacyNoticeChannel.APP}>App</Option>
            <Option value={PrivacyNoticeChannel.APPLICATION_FORM}>Bewerbungsformular</Option>
            <Option value={PrivacyNoticeChannel.EMPLOYMENT}>Beschäftigung</Option>
            <Option value={PrivacyNoticeChannel.NEWSLETTER}>Newsletter</Option>
            <Option value={PrivacyNoticeChannel.CUSTOM}>Benutzerdefiniert</Option>
          </Select>
        </div>

        <div style={{ flex: '0 0 200px' }}>
          <Text style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>Status</Text>
          <Select onChange={(e: any) => setStatusFilter(e.detail.selectedOption.value)} style={{ width: '100%' }}>
            <Option value="">Alle Status</Option>
            <Option value={PrivacyNoticeStatus.DRAFT}>Entwurf</Option>
            <Option value={PrivacyNoticeStatus.IN_REVIEW}>In Prüfung</Option>
            <Option value={PrivacyNoticeStatus.APPROVED}>Freigegeben</Option>
            <Option value={PrivacyNoticeStatus.ARCHIVED}>Archiviert</Option>
          </Select>
        </div>

        {(searchTerm || channelFilter || statusFilter) && (
          <Button
            icon="decline"
            design="Transparent"
            onClick={() => { setSearchTerm(''); setChannelFilter(''); setStatusFilter(''); }}
            tooltip="Filter zurücksetzen"
          />
        )}
      </FlexBox>

      {/* Table Card */}
      <Card style={{ overflow: 'hidden' }}
        header={
          <CardHeader
            titleText={`${filteredNotices.length} von ${notices.length} Datenschutzhinweisen`}
            subtitleText="Klicken Sie auf eine Zeile für Details"
            avatar={<Icon name="document-text" />}
          />
        }
      >
        {loading ? (
          <FlexBox alignItems="Center" justifyContent="Center" style={{ padding: '3rem' }}>
            <BusyIndicator active size="Large" />
          </FlexBox>
        ) : filteredNotices.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <Icon name="document-text" style={{ fontSize: '3rem', color: 'var(--sapNeutralColor)', display: 'block', marginBottom: '1rem' }} />
            <Title level="H5" style={{ marginBottom: '0.5rem' }}>
              {notices.length === 0 ? 'Noch keine Datenschutzhinweise' : 'Keine Treffer'}
            </Title>
            <Text style={{ display: 'block', color: 'var(--sapNeutralTextColor)', marginBottom: '1rem' }}>
              {notices.length === 0
                ? 'Erstellen Sie Ihren ersten Datenschutzhinweis.'
                : 'Passen Sie Ihre Filterkriterien an.'}
            </Text>
            {notices.length === 0 && canCreate && (
              <Button icon="add" design="Emphasized" onClick={() => navigate('/privacy-notices/new')}>
                Neuer Datenschutzhinweis erstellen
              </Button>
            )}
          </div>
        ) : (
          <div style={{ padding: '0 1rem 1rem' }}>
            <Table
              style={{ width: '100%' }}
              onRowClick={(e: any) => {
                const index = parseInt(e.detail.row.dataset.index);
                navigate(`/privacy-notices/${filteredNotices[index].id}`);
              }}
              columns={
                <>
                  <TableColumn><Text style={{ fontWeight: '600' }}>Titel</Text></TableColumn>
                  <TableColumn><Text style={{ fontWeight: '600' }}>Kanal</Text></TableColumn>
                  <TableColumn demandPopin minWidth={500}><Text style={{ fontWeight: '600' }}>Sprache</Text></TableColumn>
                  <TableColumn><Text style={{ fontWeight: '600' }}>Status</Text></TableColumn>
                  <TableColumn demandPopin minWidth={500}><Text style={{ fontWeight: '600' }}>Version</Text></TableColumn>
                  <TableColumn demandPopin minWidth={600}><Text style={{ fontWeight: '600' }}>Aktualisiert</Text></TableColumn>
                  <TableColumn demandPopin minWidth={700}><Text style={{ fontWeight: '600' }}>Veraltet</Text></TableColumn>
                </>
              }
            >
              {filteredNotices.map((notice, index) => (
                <TableRow key={notice.id} data-index={index} type="Active">
                  <TableCell>
                    <div>
                      <Text style={{ fontWeight: '600' }}>{notice.title}</Text>
                      <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginTop: '0.125rem' }}>
                        {channelLabels[notice.channel]}
                      </Text>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Text>{channelLabels[notice.channel]}</Text>
                  </TableCell>
                  <TableCell>
                    <Text>{notice.language.toUpperCase()}</Text>
                  </TableCell>
                  <TableCell>
                    <ObjectStatus state={statusConfig[notice.status].state as any} showDefaultIcon>
                      {statusConfig[notice.status].label}
                    </ObjectStatus>
                  </TableCell>
                  <TableCell>
                    <Text>
                      {notice.current_version
                        ? `v${notice.current_version.version_number}`
                        : '-'}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text>{new Date(notice.updated_at).toLocaleDateString('de-DE')}</Text>
                  </TableCell>
                  <TableCell>
                    {notice.is_outdated === true ? (
                      <Icon
                        name="alert"
                        style={{ color: 'var(--sapCriticalColor, #e9730c)', fontSize: '1.25rem' }}
                        title="Datenschutzhinweis ist veraltet"
                      />
                    ) : (
                      <Text style={{ color: 'var(--sapNeutralTextColor)' }}>-</Text>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
